import { makeStyles, MenuItem, Select } from '@material-ui/core';
import { cloneDeep } from 'lodash';
import { isEmpty } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IFilterPillIcon } from '~/src/dash/components/filters/common';
import { SortByMode } from '~/src/dash/components/material-search-dropdown-multi-list';
import { Col, Cols, Div, Text, TKey, usePublicaTranslation } from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';

const useStyles = makeStyles(() => {
  return {
    select: {
      paddingTop: '5px',
      paddingRight: '16px !important',
      paddingBottom: '5px',
      paddingLeft: '12px',
      '&:focus': {
        backgroundColor: 'inherit !important',
        borderRadius: '6px',
      },
    },
  };
});

const SHOW_ALL_ID = '0';

interface IFilterPillSingleProps {
  activateFilterClear: Record<string, boolean>; // this is a faux object we send to trigger a clear. ex: { clear: true }
  cypressParentName: string;
  defaultSelected?: string[]; // array of ids that are pre-selected // do not implement until user settings is complete
  handleFilterAccept: (menuTitle: string, list: IMenuList) => void;
  icon?: IFilterPillIcon; // icon info object
  list: IMenuList;
  menuTitle: string; // keep this plural ex: 'Countries', singularTitle ex: 'Country'
  singularTitle?: string; // see above
  sortMode?: SortByMode;
  tKey?: TKey;
}

export const FilterPillSingle = ({
  activateFilterClear,
  cypressParentName,
  defaultSelected,
  handleFilterAccept,
  icon,
  list,
  menuTitle,
  singularTitle,
  tKey,
}: IFilterPillSingleProps) => {
  const { t } = usePublicaTranslation();
  // declare states and refs
  const isMounted = useRef(false);
  const [selected, setSelected] = useState(
    defaultSelected && !isEmpty(defaultSelected) ? defaultSelected[0] : SHOW_ALL_ID,
  );
  const [isHovered, setIsHovered] = useState<boolean>(false);

  useEffect(() => {
    if (defaultSelected && defaultSelected[0] !== SHOW_ALL_ID) {
      handleFilterAccept(menuTitle, { [selected]: list[selected] });
    }
  }, []);

  useEffect(() => {
    if (isMounted.current && activateFilterClear.clear) {
      unstable_batchedUpdates(() => {
        setSelected(SHOW_ALL_ID);
      });
    }
  }, [activateFilterClear]);

  useEffect(() => {
    if (isMounted.current) {
      if (selected === SHOW_ALL_ID) {
        handleFilterAccept(menuTitle, {});
      } else {
        const selectedItem = cloneDeep(list[selected]);
        selectedItem.selected = true;
        handleFilterAccept(menuTitle, { [selected]: selectedItem });
      }
    }
    isMounted.current = true;
  }, [selected]);

  const classes = useStyles();

  const handleChange = (e: any) => {
    unstable_batchedUpdates(() => {
      setIsHovered(false);
      setSelected(e.target.value as string);
    });
  };

  const isShowAll = selected === SHOW_ALL_ID;

  const displayedTitle = tKey ? t(tKey) : menuTitle;

  return (
    <Div
      onHover={() => setIsHovered(true)}
      onLeave={() => setIsHovered(false)}
      style={{ marginRight: '8px' }}
    >
      <Select
        classes={{ select: classes.select }}
        className={`cypress-${cypressParentName}-filterpillsingle-${menuTitle}`}
        name={menuTitle}
        value={selected}
        style={{
          borderRadius: '6px',
          backgroundColor: !isShowAll
            ? publicaColors.honeydew
            : isHovered
            ? publicaColors.lightGray
            : publicaColors.veryLightGray,
        }}
        disableUnderline
        inputProps={{
          IconComponent: () => null,
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
        }}
        onChange={handleChange}
        onClose={() => setIsHovered(false)}
        renderValue={(value: string) => {
          const isShowAll = list[value].ID === SHOW_ALL_ID;
          return (
            <Cols gap='4px' style={{ alignItems: 'center' }}>
              {icon && (
                <Col style={{ verticalAlign: 'middle' }}>
                  <icon.SvgComponent
                    color={isShowAll ? publicaColors.black : publicaColors.jungleGreen}
                    style={icon.style}
                  />
                </Col>
              )}
              <Col>
                <Text
                  color={isShowAll ? publicaColors.black : publicaColors.jungleGreen}
                  extraSmall
                  bold
                  style={{ ...(icon?.labelStyle || {}) }}
                >
                  {`${singularTitle || displayedTitle} ${isShowAll ? '' : list[value].title}`}
                </Text>
              </Col>
            </Cols>
          );
        }}
      >
        {Object.values(list).map((item) => (
          <MenuItem disableRipple value={item.ID}>
            {item.title}
          </MenuItem>
        ))}
      </Select>
    </Div>
  );
};
